import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../context/UserContext';
import API from '../config/axiosConfig';
import '../pages/Settings.scss';
import './CrmSettings.scss';

// Super-admin only. Reads GET /api/crm/departments — one row per department,
// with its hospital, its admins, and the user / waiting-to-join counts.

type CrmAdmin = {
  userId: string;
  name: string;
  email: string;
  joinedAt: string | null;
};

type CrmDepartmentRow = {
  departmentId: string;
  name: string;
  createdAt: string | null;
  hospitalId: string | null;
  hospitalNameHebrew: string;
  hospitalNameEnglish: string;
  admins: CrmAdmin[];
  usersCount: number;
  pendingCount: number;
  pendingInvites: number;
  pendingJoinRequests: number;
};

type SortKey = 'hospital' | 'department' | 'createdAt' | 'admins' | 'users' | 'pending';

const DEFAULT_ERROR_MESSAGE = 'שגיאה בטעינת נתוני קשרי הלקוחות. אנא נסה שנית.';
const SEARCH_DEBOUNCE_MS = 400;

const formatDate = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB');
};

const CrmSettings: React.FC = () => {
  const { user } = useContext(UserContext);
  const isSuperAdmin = Boolean(user && user.isSuperAdmin);

  const [departments, setDepartments] = useState<CrmDepartmentRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('hospital');
  const [sortDesc, setSortDesc] = useState<boolean>(false);

  // Debounced so typing doesn't re-sort and re-render the whole table per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await API.get<{ departments: CrmDepartmentRow[] }>('/api/crm/departments', {
        handlingErrorLocally: true,
      });
      setDepartments(response.data?.departments || []);
    } catch (err: unknown) {
      console.error('Loading CRM overview failed:', err);
      const apiError = err as { response?: { data?: { error?: { hebMessage?: string; message?: string } } } };
      setError(
        apiError.response?.data?.error?.hebMessage
        || apiError.response?.data?.error?.message
        || DEFAULT_ERROR_MESSAGE
      );
      setDepartments([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return;
    load();
  }, [isSuperAdmin, load]);

  const rows = useMemo(() => {
    // The whole table arrives in one response (hundreds of departments at most),
    // so filtering and sorting stay on the client.
    const filtered = search
      ? departments.filter((row) => {
        const haystack = [
          row.hospitalNameHebrew,
          row.hospitalNameEnglish,
          row.name,
          ...row.admins.map((admin) => `${admin.name} ${admin.email}`),
        ].join(' ').toLowerCase();
        return haystack.includes(search);
      })
      : departments;

    const direction = sortDesc ? -1 : 1;
    const value = (row: CrmDepartmentRow): number | string => {
      switch (sortKey) {
        case 'department': return row.name || '';
        case 'admins': return row.admins.length;
        case 'users': return row.usersCount;
        case 'pending': return row.pendingCount;
        case 'createdAt': return row.createdAt ? new Date(row.createdAt).getTime() : 0;
        default: return row.hospitalNameHebrew || '';
      }
    };

    return [...filtered].sort((a, b) => {
      const left = value(a);
      const right = value(b);
      const primary = (typeof left === 'string' || typeof right === 'string')
        ? String(left).localeCompare(String(right), 'he') * direction
        : (left - right) * direction;
      if (primary !== 0) return primary;
      // Departments of the same hospital always stay together and alphabetical,
      // whichever column the table is sorted by.
      return (a.hospitalNameHebrew || '').localeCompare(b.hospitalNameHebrew || '', 'he')
        || (a.name || '').localeCompare(b.name || '', 'he');
    });
  }, [departments, search, sortKey, sortDesc]);

  const totals = useMemo(() => ({
    hospitals: new Set(rows.map((row) => row.hospitalId)).size,
    admins: rows.reduce((sum, row) => sum + row.admins.length, 0),
    users: rows.reduce((sum, row) => sum + row.usersCount, 0),
    pending: rows.reduce((sum, row) => sum + row.pendingCount, 0),
  }), [rows]);

  if (!isSuperAdmin) {
    return null;
  }

  const toggleSort = (nextKey: SortKey) => {
    if (nextKey === sortKey) {
      setSortDesc((previous) => !previous);
      return;
    }
    setSortKey(nextKey);
    // Names read best A→Z; the numeric columns are almost always asked
    // "who has the most" first.
    setSortDesc(nextKey !== 'hospital' && nextKey !== 'department');
  };

  const header = (columnKey: SortKey, label: string) => (
    <th>
      <button type="button" className="crm-sort-button" onClick={() => toggleSort(columnKey)}>
        {label}
        {sortKey === columnKey && <span className="crm-sort-arrow">{sortDesc ? '▼' : '▲'}</span>}
      </button>
    </th>
  );

  return (
    <div className="settings-section crm-settings">
      <h2 className="section-title">קשרי לקוחות</h2>
      <p className="crm-description">
        שורה לכל מחלקה בפלטפורמה: בית החולים שאליו היא שייכת, מועד פתיחתה, מנהלי המחלקה
        ומועד הצטרפותם, כמות המשתמשים במחלקה וכמות הממתינים להצטרף אליה
        (הזמנות ובקשות שטרם אושרו, ללא הזמנות שפג תוקפן). בתי חולים ללא מחלקות אינם מופיעים.
      </p>

      <div className="crm-toolbar">
        <input
          type="text"
          className="input-field"
          placeholder="חיפוש לפי בית חולים, מחלקה או מנהל"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="חיפוש"
        />
        <button type="button" className="button-primary" onClick={load} disabled={isLoading}>
          רענן
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading && <div className="loading">טוען...</div>}

      {!isLoading && !error && rows.length === 0 && (
        <div className="no-data">לא נמצאו מחלקות</div>
      )}

      {!isLoading && rows.length > 0 && (
        <>
          <div className="crm-summary">
            {rows.length} מחלקות ב־{totals.hospitals} בתי חולים · {totals.admins} מנהלים ·{' '}
            {totals.users} משתמשים · {totals.pending} ממתינים להצטרפות
          </div>

          <div className="crm-table-wrapper">
            <table className="hospitals-table crm-table">
              <thead>
                <tr>
                  {header('hospital', 'בית חולים')}
                  {header('department', 'מחלקה')}
                  {header('createdAt', 'תאריך פתיחה')}
                  {header('admins', 'מנהלים')}
                  {header('users', 'משתמשים')}
                  {header('pending', 'ממתינים להצטרפות')}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  // Consecutive departments of the same hospital repeat its name
                  // quietly, so the eye groups them without losing the value when
                  // the table is sorted by another column.
                  const repeatsHospital = index > 0 && rows[index - 1].hospitalId === row.hospitalId;

                  return (
                    <tr key={row.departmentId} className={repeatsHospital ? undefined : 'crm-hospital-start'}>
                      <td className="crm-cell-hospital">
                        <div className={repeatsHospital ? 'crm-hospital-repeat' : 'crm-hospital-name'}>
                          {row.hospitalNameHebrew || '—'}
                        </div>
                        {!repeatsHospital && row.hospitalNameEnglish && (
                          <div className="crm-hospital-key">{row.hospitalNameEnglish}</div>
                        )}
                      </td>

                      <td className="crm-cell-department">{row.name || '—'}</td>

                      <td className="crm-cell-date">{formatDate(row.createdAt)}</td>

                      <td className="crm-cell-admins">
                        {row.admins.length === 0 ? (
                          <span className="crm-empty">אין מנהלים</span>
                        ) : (
                          <table className="crm-admins-table">
                            <thead>
                              <tr>
                                <th>שם</th>
                                <th>אימייל</th>
                                <th>הצטרף</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.admins.map((admin) => (
                                <tr key={admin.userId}>
                                  <td>{admin.name || '—'}</td>
                                  <td className="crm-admin-email">{admin.email || '—'}</td>
                                  <td className="crm-cell-date">{formatDate(admin.joinedAt)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>

                      <td className="crm-cell-number">{row.usersCount}</td>

                      <td className="crm-cell-number">
                        <span className={row.pendingCount > 0 ? 'crm-pending' : undefined}>
                          {row.pendingCount}
                        </span>
                        {row.pendingCount > 0 && (
                          <div className="crm-pending-breakdown">
                            {row.pendingInvites} הזמנות · {row.pendingJoinRequests} בקשות
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default CrmSettings;
